import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit, Optional, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteOrigin,
  MatAutocompleteTrigger,
  MatOptgroup,
  MatOption,
} from '@angular/material/autocomplete';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, merge, shareReplay, startWith, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { HomeComponent } from '../../../pages/home/home.component';
import { ItemsModalComponent } from '../../../pages/items/items-modal/items-modal.component';
import { IItemBase } from '../../../pages/items/items.interface';
import { ESearchMode } from './global-search.interface';
import { GlobalSearchStore, ISearchResult } from './global-search.store';

@Component({
  imports: [
    MatIcon,
    TranslateModule,
    AsyncPipe,
    MatAutocomplete,
    MatAutocompleteOrigin,
    MatOptgroup,
    MatOption,
    MatAutocompleteTrigger,
    MatProgressBar,
    ReactiveFormsModule,
  ],
  providers: [GlobalSearchStore],
  selector: 'app-global-search',
  styleUrl: './global-search.component.scss',
  templateUrl: './global-search.component.html',
})
export class GlobalSearchComponent implements OnInit, OnDestroy {
  public static readonly MIN_SEARCH_CHARS = 3;

  public readonly minSearchChars = GlobalSearchComponent.MIN_SEARCH_CHARS;
  public readonly searchMode = signal<ESearchMode>(ESearchMode.minimized);
  public readonly saveChanges: string = this.translate.instant('button.saveChanges');
  public readonly searchControl: FormControl<string> = new FormControl();
  public readonly searchObs$ = this.searchControl.valueChanges.pipe(
    startWith(''),
    distinctUntilChanged(),
    debounceTime(600),
    shareReplay(1),
  );

  private readonly clearResultsOnShortQuery$ = this.searchControl.valueChanges.pipe(
    filter((value) => !value || value.length < GlobalSearchComponent.MIN_SEARCH_CHARS),
    map(() => []),
  );

  public readonly actionSearchResults$ = merge(
    this.searchStore.select((sState) => sState.searchFields.action).pipe(map((searchData) => searchData.results)),
    this.clearResultsOnShortQuery$,
  );

  public readonly boardSearchResults$ = merge(
    this.searchStore.select((sState) => sState.searchFields.boards).pipe(map((searchData) => searchData.results)),
    this.clearResultsOnShortQuery$,
  );

  public readonly issueSearchResults$ = merge(
    this.searchStore.select((sState) => sState.searchFields.issue).pipe(map((searchData) => searchData.results)),
    this.clearResultsOnShortQuery$,
  );

  public readonly itemCommentSearchResults$ = merge(
    this.searchStore.select((sState) => sState.searchFields.itemComment).pipe(map((searchData) => searchData.results)),
    this.clearResultsOnShortQuery$,
  );

  public readonly itemValueSearchResults$ = merge(
    this.searchStore.select((sState) => sState.searchFields.itemValue).pipe(map((searchData) => searchData.results)),
    this.clearResultsOnShortQuery$,
  );

  public readonly itemDescriptionSearchResults$ = merge(
    this.searchStore
      .select((sState) => sState.searchFields.itemDescription)
      .pipe(map((searchData) => searchData.results)),
    this.clearResultsOnShortQuery$,
  );

  public ESearchMode: typeof ESearchMode = ESearchMode;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly eRef: ElementRef,
    public readonly searchStore: GlobalSearchStore,
    @Optional() private readonly dialogRef: MatDialogRef<GlobalSearchComponent> | null,
  ) {}

  @HostBinding('class.in-dialog')
  public get isInDialog(): boolean {
    return this.dialogRef !== null;
  }

  public ngOnInit(): void {
    this.subscriptions.push(
      this.searchObs$
        .pipe(filter((search) => search !== null && search?.length >= GlobalSearchComponent.MIN_SEARCH_CHARS))
        .subscribe((search: string | null): void => {
          this.searchStore.loadInitialSearch(search!);
        }),
    );
  }

  @HostListener('document:click', ['$event'])
  public clickOut(event: Event): void {
    const eventTarget: HTMLElement = event.target as HTMLElement;

    if (!document.body.contains(eventTarget)) {
      return;
    }

    const bypassMinimizeRequest: boolean =
      eventTarget.closest('.mat-mdc-autocomplete-panel') !== null ||
      eventTarget.closest('.cdk-overlay-container') !== null;

    if (
      !(this.eRef.nativeElement as HTMLElement).contains(eventTarget) &&
      !bypassMinimizeRequest &&
      this.searchMode() === ESearchMode.extended
    ) {
      this.searchMode.set(ESearchMode.minimized);
      event.stopPropagation();
    }
  }

  public loadMoreSearchResults(field: keyof ISearchResult, $event?: Event): void {
    if ($event) {
      $event.stopPropagation();
    }

    this.searchStore.loadMoreSearchResults({ field, searchText: this.searchControl.value! });
  }

  public openItemEditModal(itemId: number | undefined, clearSearchText = false): void {
    if (!itemId) {
      return;
    }

    if (clearSearchText) {
      this.searchControl.setValue('');
    }

    this.dialogRef?.close();

    HomeComponent.afterItemEditCompletedDynamic.switch(
      this.dialog
        .open(ItemsModalComponent, {
          ...ItemsModalComponent.modalParameters,
          data: {
            idForEdit: itemId,
            submitButtonText: this.saveChanges,
          },
          restoreFocus: false,
        })
        .afterClosed(),
    );
  }

  public expandSearch(): void {
    this.searchMode.set(ESearchMode.extended);
  }

  public getSnippetHtml(value: string | null | undefined): string {
    const text: string = this.stripHtml(value ?? '');
    const search: string = (this.searchControl.value ?? '').trim();

    if (!text) {
      return '';
    }

    const matchIndex: number = search ? text.toLowerCase().indexOf(search.toLowerCase()) : -1;

    if (matchIndex === -1) {
      return this.escapeHtml(text);
    }

    const contextLength = 10;
    const matchEnd: number = matchIndex + search.length;
    const start: number = Math.max(0, matchIndex - contextLength);
    const end: number = Math.min(text.length, matchEnd + contextLength);

    const prefix: string = start > 0 ? '...' : '';
    const suffix: string = end < text.length ? '...' : '';
    const before: string = this.escapeHtml(text.slice(start, matchIndex));
    const match: string = this.escapeHtml(text.slice(matchIndex, matchEnd));
    const after: string = this.escapeHtml(text.slice(matchEnd, end));

    return `${prefix}${before}<strong>${match}</strong>${after}${suffix}`;
  }

  public getMatchedValueField(item: IItemBase): { fieldName: string; value: string } | null {
    const search: string = (this.searchControl.value ?? '').toLowerCase().trim();

    if (!search || !item?.values?.length) {
      return null;
    }

    for (const entry of item.values) {
      const { selectedValue } = entry;

      if (Array.isArray(selectedValue)) {
        const matches = selectedValue.filter((option) => option?.name?.toLowerCase().includes(search));

        if (matches.length) {
          return { fieldName: entry.fieldName, value: matches.map((option) => option.name).join(', ') };
        }
      } else if (typeof selectedValue === 'string' && selectedValue.toLowerCase().includes(search)) {
        return { fieldName: entry.fieldName, value: selectedValue };
      }
    }

    return null;
  }

  public navigateToBoard(boardKey: string): void {
    this.dialogRef?.close();
    this.router.navigate([`/items/${boardKey}/all`]);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
  }

  private stripHtml(html: string): string {
    const withoutTags: string = html.replace(/<[^>]*>/g, ' ');
    const decoder: HTMLTextAreaElement = document.createElement('textarea');
    decoder.innerHTML = withoutTags;

    return (decoder.value || '').replace(/\s+/g, ' ').trim();
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
